import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTheme } from '../contexts/ThemeContext';
import { usePoints } from '../contexts/PointsContext';
import { downloadFullJsonExport, downloadTasksCsv, parseJsonImport } from '../common/dataPortability';
import { getAutoArchiveDayOptions } from '../common/autoArchiveSettings';
import {
  getStoredShortcuts,
  setStoredShortcuts,
  resetStoredShortcuts,
  getShortcutsEnabled,
  setShortcutsEnabled,
  formatShortcutLabel
} from '../common/keyboardShortcutsStorage';
import ShortcutCapture from './common/ShortcutCapture';

const {
  FiSun, FiMoon, FiAward, FiGlobe, FiInfo,
  FiVolume2, FiVolumeX, FiArchive, FiDownload, FiUpload, FiDatabase,
  FiKeyboard
} = FiIcons;

const Settings = ({
  tasks = [],
  projects = [],
  tags = [],
  setTasks,
  setProjects,
  setTags,
  autoArchiveSettings,
  onAutoArchiveSettingsChange
}) => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { pointsBreakdown } = usePoints();
  const { t, i18n } = useTranslation();
  const importInputRef = React.useRef(null);
  const [completionSoundEnabled, setCompletionSoundEnabled] = React.useState(() => {
    const stored = localStorage.getItem('completionSoundEnabled');
    return stored !== null ? stored === 'true' : true;
  });
  const [importError, setImportError] = React.useState('');
  const [shortcutsEnabled, setShortcutsEnabledState] = React.useState(getShortcutsEnabled);
  const [customShortcuts, setCustomShortcuts] = React.useState(getStoredShortcuts);
  const [editingShortcut, setEditingShortcut] = React.useState(null);

  React.useEffect(() => {
    localStorage.setItem('completionSoundEnabled', completionSoundEnabled);
  }, [completionSoundEnabled]);

  const languages = [
    { code: 'en', label: 'EN', name: t('language.en') },
    { code: 'fr', label: 'FR', name: t('language.fr') },
    { code: 'es', label: 'ES', name: t('language.es') },
    { code: 'zh', label: '中文', name: t('language.zh') }
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const currentAutoArchiveSettings = autoArchiveSettings || { enabled: false, days: 30 };

  const updateAutoArchive = (updates) => {
    onAutoArchiveSettingsChange?.({
      ...currentAutoArchiveSettings,
      ...updates
    });
  };

  const handleExportJson = () => {
    downloadFullJsonExport({
      tasks,
      projects,
      tags,
      settings: {
        completionSoundEnabled,
        autoArchive: currentAutoArchiveSettings,
        language: i18n.language,
        darkMode
      }
    });
  };

  const handleExportCsv = () => {
    downloadTasksCsv(tasks, projects, tags);
  };

  const handleImportJson = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportError('');

    try {
      const imported = await parseJsonImport(file);
      const confirmed = window.confirm(t('settings.importConfirm'));

      if (!confirmed) return;

      setTasks?.(imported.tasks);
      setProjects?.(imported.projects);
      setTags?.(imported.tags);

      if (imported.settings?.autoArchive) {
        onAutoArchiveSettingsChange?.(imported.settings.autoArchive);
      }

      if (typeof imported.settings?.completionSoundEnabled === 'boolean') {
        setCompletionSoundEnabled(imported.settings.completionSoundEnabled);
      }
    } catch (error) {
      console.error('[Settings] Import failed:', error);
      setImportError(error.message);
    } finally {
      event.target.value = '';
    }
  };

  const getCurrentLanguageName = () => {
    const lang = languages.find(l => l.code === i18n.language);
    return lang ? lang.name : 'English';
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">{t('settings.title')}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t('settings.subtitle')}</p>
      </div>

      <div className="space-y-6">
        {/* Appearance Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">{t('settings.appearance')}</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SafeIcon icon={darkMode ? FiMoon : FiSun} className={darkMode ? "text-blue-400" : "text-yellow-500"} />
              <div>
                <p className="font-medium text-slate-800 dark:text-white">{t('settings.themeMode')}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {darkMode ? t('settings.darkMode') : t('settings.lightMode')}
                </p>
              </div>
            </div>
            <motion.button
              onClick={toggleDarkMode}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`relative px-10 py-3 rounded-xl ${
                darkMode ? 'bg-slate-700 text-white' : 'bg-blue-50 text-slate-800'
              }`}
            >
              <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-lg ${darkMode ? 'opacity-100' : 'opacity-0'}`}>
                <SafeIcon icon={FiMoon} />
              </span>
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-lg ${darkMode ? 'opacity-0' : 'opacity-100'}`}>
                <SafeIcon icon={FiSun} />
              </span>
              {darkMode ? t('settings.dark') : t('settings.light')}
            </motion.button>
          </div>
        </motion.div>

        {/* Language Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">{t('language.select')}</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiGlobe} className="text-blue-500" />
              <div>
                <p className="font-medium text-slate-800 dark:text-white">{t('language.select')}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {getCurrentLanguageName()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
              {languages.map((lang) => (
                <motion.button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    i18n.language === lang.code
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {lang.label}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Completion Sound Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">{t('settings.audio')}</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SafeIcon icon={completionSoundEnabled ? FiVolume2 : FiVolumeX} className="text-blue-500" />
              <div>
                <p className="font-medium text-slate-800 dark:text-white">{t('settings.completionSound')}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {completionSoundEnabled ? t('settings.soundEnabled') : t('settings.soundDisabled')}
                </p>
              </div>
            </div>
            <motion.button
              onClick={() => setCompletionSoundEnabled(v => !v)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`relative px-10 py-3 rounded-xl ${
                completionSoundEnabled ? 'bg-slate-700 text-white' : 'bg-blue-50 text-slate-800'
              }`}
            >
              <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-lg ${completionSoundEnabled ? 'opacity-100' : 'opacity-0'}`}>
                <SafeIcon icon={FiVolume2} />
              </span>
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-lg ${completionSoundEnabled ? 'opacity-0' : 'opacity-100'}`}>
                <SafeIcon icon={FiVolumeX} />
              </span>
              {completionSoundEnabled ? t('settings.on') : t('settings.off')}
            </motion.button>
          </div>
        </motion.div>

        {/* Points System Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">{t('settings.pointsSystem')}</h2>
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
            <div className="mb-2 flex items-center gap-3">
              <SafeIcon icon={FiAward} className="text-blue-500" />
              <h3 className="font-medium text-slate-800 dark:text-blue-400">{t('settings.timeUnitsTitle')}</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {t('settings.timeUnitsDescription')}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-600 dark:text-slate-300 sm:grid-cols-2">
              <div>{t('settings.timeUnitsCurrent')}: {pointsBreakdown.timeUnits} TU</div>
              <div>{t('settings.timeUnitsPointImpact')}: {pointsBreakdown.tuPoints >= 0 ? '+' : ''}{pointsBreakdown.tuPoints}</div>
            </div>
          </div>
        </motion.div>

        {/* Auto Archive Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4">{t('settings.autoArchive')}</h2>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiArchive} className="text-blue-500" />
              <div>
                <p className="font-medium text-slate-800 dark:text-white">{t('settings.autoArchiveCompleted')}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('settings.autoArchiveDescription')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={currentAutoArchiveSettings.days}
                onChange={(event) => updateAutoArchive({ days: Number(event.target.value) })}
                disabled={!currentAutoArchiveSettings.enabled}
                className="px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-800 dark:text-white disabled:opacity-50"
              >
                {getAutoArchiveDayOptions().map(days => (
                  <option key={days} value={days}>
                    {t('settings.autoArchiveAfterDays', { count: days })}
                  </option>
                ))}
              </select>
              <motion.button
                onClick={() => updateAutoArchive({ enabled: !currentAutoArchiveSettings.enabled })}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentAutoArchiveSettings.enabled
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}
              >
                {currentAutoArchiveSettings.enabled ? t('settings.on') : t('settings.off')}
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Keyboard Shortcuts Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiKeyboard} className="text-blue-500" />
              <h2 className="text-xl font-semibold text-slate-800 dark:text-white">{t('settings.shortcuts')}</h2>
            </div>
            <button
              type="button"
              onClick={() => {
                const next = !shortcutsEnabled;
                setShortcutsEnabledState(next);
                setShortcutsEnabled(next);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                shortcutsEnabled
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
              }`}
            >
              {shortcutsEnabled ? t('settings.shortcutsEnabled') : t('settings.shortcutsDisabled')}
            </button>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            {t('settings.shortcutsDescription')}
          </p>
          <div className={`space-y-3 ${!shortcutsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {Object.entries(customShortcuts).map(([key, cfg]) => (
              <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0">
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {t(cfg.labelKey)}
                </span>
                <button
                  type="button"
                  onClick={() => setEditingShortcut(key)}
                  className={`px-2 py-1 rounded-md text-xs font-mono transition-colors ${
                    editingShortcut === key
                      ? 'bg-blue-100 dark:bg-blue-900/40 border-2 border-blue-400 text-blue-700 dark:text-blue-300 animate-pulse'
                      : 'bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {editingShortcut === key ? t('settings.pressKey') || 'Appuyez...' : formatShortcutLabel(cfg)}
                </button>
              </div>
            ))}
          </div>

          {/* Shortcut capture listener */}
          {editingShortcut && (
            <ShortcutCapture
              onCapture={(newShortcut) => {
                const updated = {
                  ...customShortcuts,
                  [editingShortcut]: { ...customShortcuts[editingShortcut], ...newShortcut }
                };
                setCustomShortcuts(updated);
                setStoredShortcuts(updated);
                setEditingShortcut(null);
              }}
              onCancel={() => setEditingShortcut(null)}
            />
          )}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => {
                const reset = resetStoredShortcuts();
                setCustomShortcuts(reset);
              }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
              {t('settings.resetShortcuts')}
            </button>
          </div>
        </motion.div>

        {/* Data Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiDatabase} className="text-blue-500" />
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white">{t('settings.data')}</h2>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            {t('settings.dataDescription')}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleExportJson}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={FiDownload} />
              {t('settings.exportJson')}
            </button>
            <button
              type="button"
              onClick={handleExportCsv}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={FiDownload} />
              {t('settings.exportCsv')}
            </button>
            <button
              type="button"
              onClick={() => importInputRef.current?.click()}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={FiUpload} />
              {t('settings.importJson')}
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleImportJson}
              className="hidden"
            />
          </div>
          {importError && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
              {t('settings.importError')}: {importError}
            </p>
          )}
        </motion.div>

        {/* About Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200/60 dark:border-slate-700/60"
        >
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiInfo} className="text-blue-500" />
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white">{t('about.title')}</h2>
          </div>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {t('about.description')}
          </p>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h3 className="font-medium text-slate-800 dark:text-blue-400 mb-2">{t('about.featuresTitle')}</h3>
            <ul className="list-disc list-inside space-y-1 text-slate-600 dark:text-slate-400 text-sm">
              <li>{t('about.features.multiProject')}</li>
              <li>{t('about.features.gantt')}</li>
              <li>{t('about.features.calendar')}</li>
              <li>{t('about.features.timeboxing')}</li>
              <li>{t('about.features.points')}</li>
              <li>{t('about.features.priority')}</li>
              <li>{t('about.features.theme')}</li>
              <li>{t('about.features.subtasks')}</li>
            </ul>
          </div>
          <div className="mt-6 flex justify-start">
            <img
              src="/logos/logo-face.png"
              alt="Efficacitas"
              className="h-[336px] w-auto object-contain opacity-80"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Settings;
